import { useEffect, useState } from 'react';
import { DEV_FLAG_LABEL, type BatchCreated, type DevFlags, type DevSimulate, type Driver, type Place } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useNames } from '../lib/directory';
import { putPlace, showPlacesToDriver } from '../lib/fs';
import { useMe } from '../lib/me';
import { useStepUp } from './StepUp';
import { Dialog, useToast } from './ui';

/** 見本の場所。架空のもので、本物の取引先の情報は入れない */
const SAMPLE_PLACES = [
  { name: '見本 積み場所', address: '札幌市見本区見本 1 丁目', lat: 43.0621, lng: 141.3544 },
  { name: '見本 届け先', address: '札幌市見本区見本 2 丁目', lat: 43.0687, lng: 141.3508 },
];

export function DevUnlockDialog({ onClose, onOpen }: { onClose: () => void; onOpen: (password: string, flags: DevFlags) => void }) {
  const { ensureRecent } = useStepUp();
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const open = async () => {
    setErr('');
    if (!(await ensureRecent('開発者モード'))) return;
    try { onOpen(password, (await api.post<{ flags: DevFlags }>('/v1/office/dev-mode/unlock', { password })).flags); }
    catch (e) { setErr(e instanceof HttpError ? (e.status === 404 ? 'ここでは使えません' : e.message) : '開けませんでした'); }
  };
  return (
    <Dialog title="開発者モード" onClose={onClose}>
      <div className="field"><label htmlFor="dev-pass">合言葉</label><input id="dev-pass" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={!password} onClick={() => void open()}>開く</button></div>
    </Dialog>
  );
}

/** 解除の切り替えと、見本のデータ。合言葉はこの画面を開いているあいだだけ持つ */
export function DevPanel({ password, initial }: { password: string; initial: DevFlags }) {
  const me = useMe();
  const toast = useToast();
  const { ensureRecent } = useStepUp();
  const [flags, setFlags] = useState(initial);
  const [err, setErr] = useState('');
  const names = useNames();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverId, setDriverId] = useState('');
  useEffect(() => { api.get<{ drivers: Driver[] }>('/v1/office/drivers').then((r) => { setDrivers(r.drivers); setDriverId(r.drivers[0]?.id ?? ''); }); }, []);
  const run = async (what: string, fn: () => Promise<unknown>, done: string) => {
    setErr('');
    if (!(await ensureRecent(what))) return;
    try { await fn(); toast(done); } catch (e) { setErr(e instanceof HttpError ? e.message : 'できませんでした'); }
  };
  const simulate = (what: DevSimulate['what'], label: string) => run(label, () => api.post('/v1/office/dev-mode/simulate', { password, driverId, what }), `${label}の状態を作りました`);
  const sendRequest = () => run('見本の依頼', async () => {
    const { places } = await api.get<{ places: Place[] }>('/v1/office/places');
    const [from, to] = places;
    if (!from || !to) throw new HttpError(409, 'STATE_CONFLICT', '先に見本の場所を入れてください');
    await api.post<BatchCreated>('/v1/office/batches', { driverId, stops: [{ kind: 'pickup', placeId: from.id }, { kind: 'drop', placeId: to.id }] });
    await showPlacesToDriver(me!.officeId, driverId, [from.id, to.id]);
  }, '見本の依頼を送りました');
  const save = async (next: DevFlags) => {
    setErr('');
    if (!(await ensureRecent('開発者モードの設定'))) return;
    try { await api.post('/v1/office/dev-mode/flags', { password, flags: next }); setFlags(next); toast('設定を変えました'); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '変えられませんでした'); }
  };
  const addPlaces = async () => {
    setErr('');
    if (!(await ensureRecent('見本の場所の追加'))) return;
    try {
      for (const { name, address, lat, lng } of SAMPLE_PLACES) {
        const { id } = await api.post<{ id: string }>('/v1/office/places', { lat, lng, isFavorite: true, loadMin: 10, unloadMin: 10 });
        await putPlace(id, { officeId: me!.officeId, name, address, phone: null, memo: null });
      }
      toast('見本の場所を入れました');
    } catch (e) { setErr(e instanceof HttpError ? e.message : '入れられませんでした'); }
  };
  return (
    <section className="panel" aria-label="開発者モード" style={{ padding: 16, display: 'grid', gap: 10 }}>
      <b>開発者モード（本番にはありません）</b>
      {(Object.keys(DEV_FLAG_LABEL) as (keyof DevFlags)[]).map((k) => (
        <label key={k}><input type="checkbox" style={{ width: 'auto' }} checked={flags[k]} onChange={(e) => void save({ ...flags, [k]: e.target.checked })} /> {DEV_FLAG_LABEL[k]}</label>
      ))}
      <button className="btn" style={{ justifySelf: 'start' }} onClick={() => void addPlaces()}>見本の場所を入れる</button>
      <div className="field"><label htmlFor="dev-driver">対象のドライバー</label>
        <select id="dev-driver" value={driverId} onChange={(e) => setDriverId(e.target.value)}>{drivers.map((d) => <option key={d.id} value={d.id}>{names.driver(d.id)}</option>)}</select></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button className="btn sm" disabled={!driverId} onClick={() => void sendRequest()}>見本の依頼を送る</button>
        <button className="btn sm" disabled={!driverId} onClick={() => void run('勤務前に戻す', () => api.post('/v1/office/dev-mode/reset', { password, driverId }), '勤務前に戻しました')}>勤務前に戻す</button>
        <button className="btn sm" disabled={!driverId} onClick={() => void simulate('drive_warn', '連続運転 3 時間 30 分')}>連続運転 3 時間 30 分</button>
        <button className="btn sm" disabled={!driverId} onClick={() => void simulate('drive_alert', '連続運転 4 時間')}>連続運転 4 時間</button>
        <button className="btn sm" disabled={!driverId} onClick={() => void simulate('unknown_stop', '理由不明の停車')}>理由不明の停車</button>
        <button className="btn sm" disabled={!driverId} onClick={() => void simulate('no_signal', '通信なし')}>通信なし</button>
      </div>
      {err && <p className="err" role="alert">{err}</p>}
    </section>
  );
}
